import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Calendar, MapPin, Mail, Facebook } from 'lucide-react';
import { CLINIC_INFO } from '../data';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

const cn = (...args) => twMerge(clsx(args));

const navItems = [
  { name: 'Forside', path: '/' },
  { name: 'Behandlinger', path: '/behandlinger' },
  { name: 'Priser', path: '/priser' },
  { name: 'Om mig', path: '/om-mig' },
  { name: 'Praktisk info', path: '/praktisk-info' },
  { name: 'Kontakt', path: '/kontakt' },
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b bg-white border-gray-100 shadow-sm'
      )}
    >
      {/* Top Header */}
      <div 
        className={cn(
          "hidden lg:block transition-all duration-300 border-b bg-gray-50 border-gray-100 text-[#3a38a4] py-2"
        )}
      >
        <div className="container mx-auto px-4 flex justify-between items-center text-xs font-medium">
          <div className="flex items-center space-x-6">
            <a 
              href={`tel:${CLINIC_INFO.phone}`} 
              className="flex items-center hover:text-blue-600 transition-colors"
            >
              <Phone className="w-3.5 h-3.5 mr-1.5" />
              {CLINIC_INFO.phone}
            </a>
              <a 
                href="https://maps.app.goo.gl/zduoinBMcwsKXgZPA"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center hover:text-blue-600 transition-colors"
              >
                <MapPin className="w-3.5 h-3.5 mr-1.5" />
                {CLINIC_INFO.address}
              </a>
          </div>
          <div className="flex items-center space-x-6">
            <a 
              href={`mailto:${CLINIC_INFO.email}`} 
              className="flex items-center hover:text-blue-600 transition-colors"
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" />
              {CLINIC_INFO.email}
            </a>
            <a 
              href="https://www.facebook.com/CDHSport/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center hover:text-blue-600 transition-colors"
              aria-label="Følg os på Facebook"
            >
              <Facebook className="w-3.5 h-3.5" />
            </a>
            <Link
              to="/bestil-tid"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-bold transition-all shadow-sm flex items-center"
            >
              <Calendar className="w-3 h-3 mr-1.5" />
              Bestil tid
            </Link>
          </div>
        </div>
      </div>

      <div className={cn(
        "container mx-auto px-4 transition-all duration-300 py-3 lg:py-4"
      )}>
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/597fff01-44e7-478d-9a63-9d32022c74eb.png" 
              alt="CDH Massage Logo" 
              className="h-20 lg:h-32 w-auto transition-all duration-300" 
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-4">
            <nav className="flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'px-4 py-2 rounded-full text-sm font-medium transition-all duration-200',
                    location.pathname === item.path
                      ? 'bg-blue-50 text-[#3a38a4]'
                      : 'text-[#3a38a4]/80 hover:text-[#3a38a4] hover:bg-gray-50'
                  )}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            <Link
              to="/bestil-tid"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-full text-sm font-bold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Online Booking
            </Link>
          </div>

          {/* Mobile Actions */}
          <div className="flex items-center space-x-2 lg:hidden">
            <Link
              to="/bestil-tid"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-full text-xs font-bold transition-all shadow-sm flex items-center"
            >
              <Calendar className="w-3.5 h-3.5 mr-1.5" />
              Book tid
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                "p-2 rounded-lg transition-colors text-[#3a38a4] hover:bg-gray-50"
              )}
              aria-label="Toggle Menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav Overlay */}
      <div
        className={cn(
          'fixed inset-0 top-[65px] bg-white z-40 transition-transform duration-300 lg:hidden overflow-y-auto',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="container mx-auto px-4 py-8 flex flex-col space-y-4">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'text-lg font-medium p-4 rounded-xl transition-colors',
                location.pathname === item.path
                  ? 'bg-blue-50 text-[#3a38a4]'
                  : 'text-gray-700 active:bg-gray-50'
              )}
            >
              {item.name}
            </Link>
          ))}
          <div className="pt-8 space-y-4">
            <Link
              to="/bestil-tid"
              className="block w-full text-center bg-[#3a38a4] text-white py-4 rounded-xl font-bold shadow-md"
            >
              Bestil tid
            </Link>
            <a
              href={`tel:${CLINIC_INFO.phone}`}
              className="block w-full text-center border-2 border-gray-100 text-[#3a38a4] py-4 rounded-xl font-bold"
            >
              Ring: {CLINIC_INFO.phone}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
